import React, { useState, useEffect } from 'react';
import { Mail, MapPin, X, Briefcase, GraduationCap, Phone } from 'lucide-react';
import { Headshot } from '../components/Headshot';

const OurTeam = () => {
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => {
    // Handle anchor links
    const hash = window.location.hash;
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, []);

  const attorneys = [
    {
      name: 'Mike Rizzi',
      title: 'Partner',
      photo: 'mike',
      licensed: 'North Carolina',
      focus: 'NC workers\' compensation cases. Assists with auto, catastrophic injury, trucking, and wrongful death cases. Firm administration.',
      education: 'B.A. in Economics, Bucknell University; J.D., Campbell University School of Law',
      phone: '336-354-1040',
      email: 'MRizzi@rizzilawgroup.com',
    },
    {
      name: 'Doug Winn',
      title: 'Partner',
      photo: 'doug',
      imagePosition: 'top',
      licensed: 'North Carolina',
      focus: 'Workers\' compensation',
      education: 'B.A., The University of Georgia; J.D., Wake Forest University School of Law',
      phone: '336-354-1040',
      email: 'DWinn@rizzilawgroup.com',
    },
    {
      name: 'Taylor Gordon',
      title: 'Partner',
      photo: 'taylor',
      licensed: 'North Carolina, South Carolina',
      focus: 'Auto accidents, catastrophic injury, premises liability, trucking accidents, and wrongful death cases.',
      phone: '336-354-1040',
      email: 'TGordon@rizzilawgroup.com',
    },
    {
      name: 'Ashton Gordon',
      title: 'Partner',
      photo: 'ashton',
      licensed: 'North Carolina, South Carolina',
      focus: 'Children\'s injury cases. Firm administration.',
      education: 'B.A. in Psychology, Wake Forest University; J.D., Wake Forest University School of Law',
      phone: '336-354-1040',
      email: 'AGordon@rizzilawgroup.com',
    },
    {
      name: 'Alex Sherret',
      title: 'Attorney',
      photo: 'alex',
      licensed: 'North Carolina, South Carolina, Tennessee',
      focus: 'Auto accidents, catastrophic injury, premises liability, trucking accidents, and wrongful death cases.',
      education: 'B.A. in Sports Management, Liberty University; J.D., Liberty University School of Law',
      phone: '336-354-1040',
      email: 'ASherret@rizzilawgroup.com',
    },
  ];

  const staff = [
    {
      name: 'Amy Anderson',
      title: 'Paralegal',
      credentials: 'NC Certified Paralegal',
      focus: 'Auto, catastrophic injury, premises liability, trucking, and wrongful death cases for NC and SC clients.',
      phone: '(336) 354-0024',
      email: 'aanderson@rizzilawgroup.com',
    },
    {
      name: 'LeighAnne Mataranglo',
      title: 'Paralegal',
      focus: 'Auto, catastrophic injury, premises liability, trucking, and wrongful death cases for NC and SC clients.',
      phone: '(864) 265-7959',
      email: 'lmataranglo@rizzilawgroup.com',
    },
    {
      name: 'Erica Oliver',
      title: 'Paralegal',
      focus: 'Auto, catastrophic injury, premises liability, trucking, and wrongful death cases for NC and SC clients.',
      phone: '(336) 354-1041',
      email: 'eoliver@rizzilawgroup.com',
    },
    {
      name: 'Olivia Teiken',
      title: 'Paralegal',
      focus: 'Auto, catastrophic injury, premises liability, trucking, and wrongful death cases for NC and SC clients.',
      phone: '(864) 774-0855',
      email: 'oteiken@rizzilawgroup.com',
    },
    {
      name: 'Rebecca Frazier-Easter',
      title: 'Medical Records Specialist',
      focus: 'NC workers\' compensation cases.',
      phone: '(864) 334-6358',
      email: 'rfrazier@rizzilawgroup.com',
    },
    {
      name: 'Sarah Swofford Starnes',
      title: 'Paralegal',
      focus: 'NC workers\' compensation cases.',
      phone: '(743) 270-0003',
      email: 'sswofford@rizzilawgroup.com',
    },
  ];

  const TeamMemberCard = ({ member, isAttorney }) => (
    <div
      className="border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow duration-300 cursor-pointer"
      onClick={() => setSelectedMember({ ...member, isAttorney })}
      data-testid={`team-member-${member.name.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="aspect-[3/4] overflow-hidden bg-slate-100">
        <Headshot
          name={member.photo}
          alt={member.name}
          sizes="(min-width:1024px) 400px, (min-width:640px) 45vw, 90vw"
          className="w-full h-full object-cover object-top"
        />
      </div>
      <div className="p-4 md:p-6">
        <h3 className="text-base md:text-xl font-heading font-semibold text-slate-900 mb-1">
          {member.name}
        </h3>
        <p className="text-xs md:text-sm text-primary font-medium mb-2">{member.title}</p>
        {isAttorney && member.licensed && (
          <p className="text-xs md:text-sm text-slate-600 mb-2 md:mb-3">Licensed: {member.licensed}</p>
        )}
        {!isAttorney && member.credentials && (
          <p className="text-xs md:text-sm text-slate-600 mb-2 md:mb-3">{member.credentials}</p>
        )}
        {isAttorney && member.focus && (
          <div className="flex items-start gap-1.5 md:gap-2 text-xs md:text-sm text-slate-600 mt-2 md:mt-3">
            <Briefcase className="h-3 w-3 md:h-4 md:w-4 flex-shrink-0 mt-0.5" />
            <span className="line-clamp-2">{member.focus}</span>
          </div>
        )}
        <div className="flex items-center gap-1.5 md:gap-2 text-xs md:text-sm text-slate-600 mt-1.5 md:mt-2">
          <Mail className="h-3 w-3 md:h-4 md:w-4 flex-shrink-0" />
          <a href={`mailto:${member.email}`} className="hover:text-primary break-all text-[11px] md:text-sm">
            {member.email}
          </a>
        </div>
      </div>
    </div>
  );

  const StaffMemberCard = ({ member }) => (
    <div
      className="border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow duration-300 cursor-pointer bg-white"
      onClick={() => setSelectedMember({ ...member, isAttorney: false })}
      data-testid={`team-member-${member.name.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex flex-col items-center pt-6 md:pt-8 pb-3 md:pb-4">
        <div
          className="w-12 h-12 md:w-16 md:h-16 rounded-full flex items-center justify-center text-white text-xl md:text-2xl font-semibold"
          style={{ backgroundColor: '#1e3a5f' }}
        >
          {member.name.charAt(0)}
        </div>
      </div>
      <div className="px-3 md:px-6 pb-4 md:pb-6 text-center">
        <h3 className="text-base md:text-xl font-heading font-semibold text-slate-900 mb-1">
          {member.name}
        </h3>
        <p className="text-xs md:text-sm text-primary font-medium mb-2">{member.title}</p>
        {member.credentials && (
          <p className="text-xs md:text-sm text-slate-600 mb-3 hidden sm:block">{member.credentials}</p>
        )}
        <div className="flex items-center justify-center gap-1.5 md:gap-2 text-xs md:text-sm text-slate-600">
          <Mail className="h-3 w-3 md:h-4 md:w-4 flex-shrink-0" />
          <a href={`mailto:${member.email}`} className="hover:text-primary break-all text-[11px] md:text-sm">
            {member.email}
          </a>
        </div>
        {member.phone && (
          <div className="flex items-center justify-center gap-1.5 md:gap-2 text-xs md:text-sm text-slate-600 mt-1.5">
            <Phone className="h-3 w-3 md:h-4 md:w-4 flex-shrink-0" />
            <a href={`tel:${member.phone.replace(/[^\d]/g, '')}`} className="hover:text-primary text-[11px] md:text-sm">
              {member.phone}
            </a>
          </div>
        )}
      </div>
    </div>
  );

  const MemberModal = () => {
    if (!selectedMember) return null;

    return (
      <div
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={() => setSelectedMember(null)}
        data-testid="team-member-modal"
      >
        <div
          className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="relative">
            <button
              onClick={() => setSelectedMember(null)}
              className="absolute top-4 right-4 bg-white rounded-full p-2 shadow-lg hover:bg-slate-100 z-10"
              data-testid="close-modal"
            >
              <X className="h-5 w-5" />
            </button>
            {selectedMember.photo ? (
              <div className="overflow-hidden bg-slate-100 flex justify-center">
                <Headshot
                  name={selectedMember.photo}
                  alt={selectedMember.name}
                  sizes="(min-width:768px) 500px, 90vw"
                  className="w-auto h-auto max-h-[400px] object-contain object-top"
                />
              </div>
            ) : (
              <div className="flex items-center justify-center py-12 bg-slate-50">
                <div
                  className="w-24 h-24 rounded-full flex items-center justify-center text-white text-4xl font-semibold"
                  style={{ backgroundColor: '#1e3a5f' }}
                >
                  {selectedMember.name.charAt(0)}
                </div>
              </div>
            )}
          </div>
          <div className="p-8">
            <h2 className="text-3xl font-heading font-bold text-slate-900 mb-2">
              {selectedMember.name}
            </h2>
            <p className="text-lg text-primary font-semibold mb-4">{selectedMember.title}</p>
            
            {selectedMember.isAttorney && selectedMember.licensed && (
              <div className="mb-4">
                <p className="text-sm font-semibold text-slate-700 mb-1">Licensed States</p>
                <p className="text-slate-600">{selectedMember.licensed}</p>
              </div>
            )}
            
            {!selectedMember.isAttorney && selectedMember.credentials && (
              <div className="mb-4">
                <p className="text-sm font-semibold text-slate-700 mb-1">Credentials</p>
                <p className="text-slate-600">{selectedMember.credentials}</p>
              </div>
            )}
            
            <div className="mb-6">
              <p className="text-sm font-semibold text-slate-700 mb-1">Primary Practice Focus</p>
              <p className="text-slate-600 leading-relaxed">{selectedMember.focus}</p>
            </div>

            {selectedMember.isAttorney && selectedMember.education && (
              <div className="mb-6">
                <p className="text-sm font-semibold text-slate-700 mb-1">Education</p>
                <div className="flex items-start gap-2 text-slate-600 leading-relaxed">
                  <GraduationCap className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span>{selectedMember.education}</span>
                </div>
              </div>
            )}
            
            <div className="border-t border-slate-200 pt-6">
              <div className="flex items-center gap-3 text-slate-700">
                <Mail className="h-5 w-5 text-primary" />
                <a href={`mailto:${selectedMember.email}`} className="hover:text-primary break-all">
                  {selectedMember.email}
                </a>
              </div>
              {!selectedMember.isAttorney && selectedMember.phone && (
                <div className="flex items-center gap-3 text-slate-700 mt-3">
                  <Phone className="h-5 w-5 text-primary" />
                  <a href={`tel:${selectedMember.phone.replace(/[^\d]/g, '')}`} className="hover:text-primary">
                    {selectedMember.phone}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="our-team-page">
      {/* Hero */}
      <section className="py-10 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-3xl sm:text-5xl md:text-6xl font-heading font-bold text-slate-900 mb-4 md:mb-6 tracking-tight">
              Our Team
            </h1>
            <p className="text-base md:text-xl text-slate-600 leading-relaxed">
              Meet the experienced attorneys and dedicated staff who will guide you through 
              every step of your case with professionalism and care.
            </p>
          </div>
        </div>
      </section>

      {/* Attorneys Section */}
      <section id="attorneys" className="py-10 md:py-24 bg-white" data-testid="attorneys-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="mb-8 md:mb-12">
            <h2 className="text-2xl md:text-4xl font-heading font-semibold text-slate-900 mb-3 md:mb-4 tracking-tight">
              Attorneys
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed max-w-3xl">
              Our attorneys bring decades of combined experience in personal injury law, 
              with multiple members of the prestigious Million Dollar Advocates Forum.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
            {attorneys.map((attorney) => (
              <TeamMemberCard key={attorney.email} member={attorney} isAttorney={true} />
            ))}
          </div>
        </div>
      </section>

      {/* Staff Section */}
      <section id="staff" className="py-10 md:py-24 bg-slate-50" data-testid="staff-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="mb-8 md:mb-12">
            <h2 className="text-2xl md:text-4xl font-heading font-semibold text-slate-900 mb-3 md:mb-4 tracking-tight">
              Paralegals & Staff
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed max-w-3xl">
              Our experienced paralegals and support staff ensure that every detail of your case 
              is handled with precision and care.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
            {staff.map((member) => (
              <StaffMemberCard key={member.email} member={member} />
            ))}
          </div>
        </div>
      </section>

      {/* Modal */}
      <MemberModal />
    </div>
  );
};

export default OurTeam;
